import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import {
  TextField, Checkbox, FormControlLabel, Button, Typography, Box,
  FormGroup, Autocomplete, Paper, Grid, Card, CardContent, CircularProgress,
  Snackbar, Alert, Chip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const INSURANCE_TYPES = ['home', 'auto', 'business', 'life'];
const CONTACT_METHODS = ['call', 'text', 'email'];
const CARRIERS = ['Intact', 'Aviva', 'Economical', 'Desjardins', 'RSA', 'Allstate', 'Belair', 'Travelers'];

const IntakeForm = () => {
  // Get event ID from URL parameters
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const [eventName, setEventName] = useState('');
  const [event, setEvent] = useState();
  const [branchLogoUrl, setBranchLogoUrl] = useState('');

  const [formData, setFormData] = useState({
    visitorName: '',
    phoneNumber: '',
    email: '',
    branchId: event ? event.branchId._id : null,
    contactMethods: [],
    insuranceTypes: [],
    currentBroker: '',
    comments: '',
    consentGiven: false,
    signature: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [touched, setTouched] = useState({
    visitorName: false,
    phoneNumber: false,
    email: false,
    contactMethods: false,
    insuranceTypes: false,
    consentGiven: false,
    signature: false
  });
  const sigCanvas = useRef();
  const [successModal, setSuccessModal] = useState({
  open: false,
  visitorName: ''
});
useEffect(() => {
    if (successModal.open) {
      const timer = setTimeout(() => {
        setSuccessModal({ open: false, visitorName: '' });
        clearForm(); // resets fields and signature
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [successModal]);
  // Fetch event name when component mounts
useEffect(() => {
  const fetchEventAndBranding = async () => {
    try {
      // Fetch event (may require auth)
      const token = localStorage.getItem('token');
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      try {
        const { data: evt } = await axios.get(`/api/events/${eventId}`, cfg);
        setEvent(evt);
        setEventName(evt?.name || 'Event');
      } catch {
        setEventName('Event'); // fallback if protected route rejects
      }

      // Always use the PUBLIC branding endpoint (no auth required)
      try {
        const { data } = await axios.get(`/api/settings/public/branding/by-event/${eventId}`);
        setBranchLogoUrl(data?.logoUrl || '');
      } catch {
        setBranchLogoUrl('');
      }
    } catch (error) {
      console.error('Failed to fetch event or branding:', error);
      setEventName('Event');
      setBranchLogoUrl('');
    }
  };

  fetchEventAndBranding();
}, [eventId]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Name is required';
    if (!/^[a-zA-Z\s]+$/.test(name)) return 'Name should contain only letters and spaces';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return 'Phone number is required';
    if (!/^\d{10}$/.test(phone)) return 'Phone must be 10 digits';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Invalid email format';
    return '';
  };

  // Live validation effect
  useEffect(() => {
    const newErrors = {};
    
    if (touched.visitorName) {
      newErrors.visitorName = validateName(formData.visitorName);
    }
    
    if (touched.phoneNumber) {
      newErrors.phoneNumber = validatePhone(formData.phoneNumber);
    }
    
    if (touched.email) {
      newErrors.email = validateEmail(formData.email);
    }
    
    if (touched.contactMethods) {
      newErrors.contactMethods = formData.contactMethods.length === 0 ? 
        'At least one contact method is required' : '';
    }
    
    if (touched.consentGiven) {
      newErrors.consentGiven = !formData.consentGiven ? 
        'You must consent to continue' : '';
    }
    
    if (touched.signature) {
      newErrors.signature = sigCanvas.current.isEmpty() ? 
        'Signature is required' : '';
    }
    
    setErrors(newErrors);
  }, [formData, touched]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Name validation
    newErrors.visitorName = validateName(formData.visitorName);
    if (newErrors.visitorName) isValid = false;

    // Phone validation
    newErrors.phoneNumber = validatePhone(formData.phoneNumber);
    if (newErrors.phoneNumber) isValid = false;

    // Email validation
    newErrors.email = validateEmail(formData.email);
    if (newErrors.email) isValid = false;

    // Contact methods validation
    if (formData.contactMethods.length === 0) {
      newErrors.contactMethods = 'At least one contact method is required';
      isValid = false;
    }

    // Insurance validation
    const hasInsuranceSelection = formData.insuranceTypes.some(
      insurance => insurance.isInsured || insurance.wantsQuote
    );
    
    const hasInteractedWithAnyInsurance = formData.insuranceTypes.some(
      insurance => insurance.isInsured || insurance.wantsQuote || insurance.carrier || insurance.renewalDate
    );
    
    if (hasInteractedWithAnyInsurance && !hasInsuranceSelection) {
      newErrors.insuranceTypes = 'For insurance types you interacted with, please select at least one option (Currently Insured or Wants Quote)';
      isValid = false;
    }

    // Consent validation
    if (!formData.consentGiven) {
      newErrors.consentGiven = 'You must consent to continue';
      isValid = false;
    }

    // Signature validation
    if (sigCanvas.current.isEmpty()) {
      newErrors.signature = 'Signature is required';
      isValid = false;
    }
    formData.insuranceTypes.forEach(insurance => {
    if (insurance.isInsured && !insurance.wantsQuote) {
      newErrors.insuranceTypes = 'If "Currently Insured" is checked, you must also select "Interested in a Quote".';
      isValid = false;
    }
  });

    setErrors(newErrors);
    setTouched({
      visitorName: true,
      phoneNumber: true,
      email: true,
      contactMethods: true,
      insuranceTypes: true,
      consentGiven: true,
      signature: true
    });
    return isValid;
  };

  const clearForm = () => {
    setFormData({
      visitorName: '',
      phoneNumber: '',
      email: '',
      contactMethods: [],
      insuranceTypes: [],
      currentBroker: '',
      comments: '',
      consentGiven: false,
      signature: ''
    });
    sigCanvas.current.clear();
    setErrors({});
    setTouched({
      visitorName: false,
      phoneNumber: false,
      email: false,
      contactMethods: false,
      insuranceTypes: false,
      consentGiven: false,
      signature: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Mark field as touched when user types
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleContactMethodChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const updated = new Set(prev.contactMethods);
      checked ? updated.add(value) : updated.delete(value);
      return { ...prev, contactMethods: Array.from(updated) };
    });
    
    if (!touched.contactMethods) {
      setTouched(prev => ({ ...prev, contactMethods: true }));
    }
    
    if (errors.contactMethods) {
      setErrors(prev => ({ ...prev, contactMethods: '' }));
    }
  };

  const handleInsuranceChange = (type, field, value) => {
    setFormData(prev => {
      let insuranceTypes = [...prev.insuranceTypes];
      let entry = insuranceTypes.find(i => i.type === type);
      if (!entry) {
        entry = { type, isInsured: false, renewalDate: '', wantsQuote: false, carrier: '' };
        insuranceTypes.push(entry);
      }
      entry[field] = value;
      if (field === 'isInsured' && !value) {
      entry.renewalDate = '';
    }
      return { ...prev, insuranceTypes };
    });
    
    if (!touched.insuranceTypes) {
      setTouched(prev => ({ ...prev, insuranceTypes: true }));
    }
    
    if (errors.insuranceTypes) {
      setErrors(prev => ({ ...prev, insuranceTypes: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    setIsSubmitting(true);
    const signature = sigCanvas.current.getCanvas().toDataURL('image/png');

    // Filter out insurance types that haven't been interacted with
    const submittedInsuranceTypes = formData.insuranceTypes.filter(insurance => 
      insurance.isInsured || insurance.wantsQuote || insurance.carrier || insurance.renewalDate
    );
    // Grab branchId directly from the fetched event
    const branchId = event?.branchId?._id;
    try {
      const submissionData = {
        ...formData,
        branchId,
        insuranceTypes: submittedInsuranceTypes,
        signature,
        eventId: eventId // Include event ID in submission
      };
      console.log('About to submit:', submissionData); 
      const token = localStorage.getItem('token');
      await axios.post('/api/submissions', submissionData,{
          headers: { Authorization: `Bearer ${token}` }
        });
      setSuccessModal({open: true,visitorName: formData.visitorName});
    } catch (err) {
      console.error('Submission error:', err);
      showSnackbar('Error submitting form. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current.clear();
    if (!touched.signature) {
      setTouched(prev => ({ ...prev, signature: true }));
    }
    if (errors.signature) {
      setErrors(prev => ({ ...prev, signature: '' }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        p: 2
      }}>
        <Paper elevation={3} sx={{ 
          width: '100%', 
          maxWidth: 800, 
          p: { xs: 2, md: 4 },
          borderRadius: 3
        }}>
          {/* Header */}
          <Box sx={{ 
            mb: 4, 
            textAlign: 'center',
            borderBottom: '1px solid #e0e0e0',
            pb: 3
          }}>
            {(branchLogoUrl || event?.branchId?.displayName) && (
            <img
              src={branchLogoUrl || '/default-logo.png'}
              alt={`${event?.branchId?.displayName || 'Branch'} Logo`}
              style={{ maxHeight: '100px', width: 'auto', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
            />
            )}
            
            {/* Event Context Display */}
            {eventId && eventName && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip 
                  label={`Event: ${eventName}`}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 600,
                    px: 2,
                    py: 1
                  }}
                />
              </Box>
            )}
            
            {eventId && !eventName && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip 
                  label="Loading event information..."
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 600,
                    px: 2,
                    py: 1
                  }}
                />
              </Box>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            {/* Contact Information Section */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  color: '#34495e'
                }}>
                  Contact Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Full Name"
                      name="visitorName"
                      value={formData.visitorName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('visitorName')}
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      error={!!errors.visitorName && touched.visitorName}
                      helperText={touched.visitorName && errors.visitorName}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Phone Number"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('phoneNumber')}
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      error={!!errors.phoneNumber && touched.phoneNumber}
                      helperText={touched.phoneNumber && errors.phoneNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('email')}
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      error={!!errors.email && touched.email}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                      Preferred Contact Method *
                    </Typography>
                    <FormGroup row sx={{ gap: 2 }}>
                      {CONTACT_METHODS.map(method => (
                        <FormControlLabel
                          key={method}
                          control={
                            <Checkbox
                              value={method}
                              checked={formData.contactMethods.includes(method)}
                              onChange={handleContactMethodChange}
                              color="primary"
                            />
                          }
                          label={method.charAt(0).toUpperCase() + method.slice(1)}
                          sx={{ mr: 0 }}
                        />
                      ))}
                    </FormGroup>
                    {touched.contactMethods && errors.contactMethods && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {errors.contactMethods}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Insurance Information Section */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  color: '#34495e'
                }}>
                  Insurance Information *
                </Typography>
                
                {touched.insuranceTypes && errors.insuranceTypes && (
                  <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {errors.insuranceTypes}
                  </Typography>
                )}
                
                {INSURANCE_TYPES.map(type => {
                  const entry = formData.insuranceTypes.find(i => i.type === type) || {};
                  return (
                    <Box 
                      key={type} 
                      sx={{ 
                        mb: 3, 
                        p: 3, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ 
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        mb: 2,
                        color: '#2c3e50'
                      }}>
                        {type} Insurance
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={entry.isInsured || false}
                                onChange={(e) => handleInsuranceChange(type, 'isInsured', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Currently Insured"
                          />
                        </Grid>
                        
                        {type !== 'life' && (
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label="Renewal/X-Date"
                              disabled={!entry.isInsured}
                              value={entry.renewalDate || null}
                              onChange={(date) => handleInsuranceChange(type, 'renewalDate', date)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  fullWidth 
                                  size="small" 
                                  variant="outlined" 
                                />
                              )}
                            />
                          </Grid>
                        )}
                        
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={entry.wantsQuote || false}
                                onChange={(e) => handleInsuranceChange(type, 'wantsQuote', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Interested in a Quote"
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            options={CARRIERS}
                            value={entry.carrier || ''}
                            onChange={(e, newValue) => handleInsuranceChange(type, 'carrier', newValue)}
                            disabled={!entry.isInsured}
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                label="Current Carrier" 
                                fullWidth 
                                size="small" 
                                variant="outlined" 
                                sx={{
                                  '& .MuiAutocomplete-inputRoot': {
                                    minWidth: '200px'
                                  }
                                }}
                              />
                            )}
                            sx={{
                              minWidth: '250px'
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
                
                <TextField
                  label="Current Broker (if applicable)"
                  name="currentBroker"
                  value={formData.currentBroker}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>

            {/* Additional Information Section */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  color: '#34495e'
                }}>
                  Additional Information
                </Typography>
                
                <TextField
                  label="Comments or Questions"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </CardContent>
            </Card>

            {/* Consent Section */}
            <Card sx={{ mb: 4, borderRadius: 2, borderLeft: '4px solid #3498db' }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  color: '#34495e'
                }}>
                  Consent Agreement *
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  mb: 3,
                  color: '#7f8c8d',
                  lineHeight: 1.6
                }}>
                  By providing your personal information on this form, you consent to BrokerUnion Insurance Inc. collecting,
                  using, and storing your information for the purposes of contacting you regarding insurance quotes, policy
                  reminders, and related services. Your information will be kept secure and confidential in accordance with PIPEDA.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.consentGiven}
                      onChange={handleInputChange}
                      name="consentGiven"
                      required
                      color="primary"
                    />
                  }
                  label="I have read and agree to the terms above and consent to being contacted by Broker Union Insurance Inc."
                  sx={{ alignItems: 'flex-start' }}
                />
                {touched.consentGiven && errors.consentGiven && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.consentGiven}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Signature Section */}
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  color: '#34495e'
                }}>
                  Electronic Signature *
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2, color: '#7f8c8d' }}>
                  Please sign in the box below
                </Typography>
                
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    width: '100%',
                    backgroundColor: '#fff'
                  }}
                >
                  <SignatureCanvas
                    penColor="black"
                    canvasProps={{ 
                      width: 800, 
                      height: 150, 
                      className: 'sigCanvas',
                      style: { width: '100%', height: 'auto' }
                    }}
                    ref={sigCanvas}
                    onEnd={() => handleBlur('signature')}
                  />
                </Box>
                {touched.signature && errors.signature && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.signature}
                  </Typography>
                )}
                
                <Button 
                  onClick={clearSignature} 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 2 }}
                >
                  Clear Signature
                </Button>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                type="submit" 
                variant="contained" 
                size="large"
                disabled={isSubmitting}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={24} sx={{ color: 'white', mr: 2 }} />
                    Submitting...
                  </>
                ) : (
                  `Submit Inquiry${eventName ? ` for ${eventName}` : ''}`
                )}
              </Button>
            </Box>
          </form>
        </Paper>
        {/* Success Modal */}
{successModal.open && (
  <>
    {/* Background Blur Overlay */}
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        zIndex: 20,
        transition: 'backdrop-filter 0.3s ease'
      }}
    />

    {/* Centered Popup Message */}
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(15px)',
        borderRadius: 4,
        boxShadow: '0 10px 35px rgba(0,0,0,0.25)',
        p: { xs: 3, md: 5 },
        width: '90%',
        maxWidth: 420,
        textAlign: 'center',
        zIndex: 30,
        animation: 'fadeIn 0.4s ease',
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{
          mb: 2,
          color: '#2e7d32',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}
      >
        ✅ Submission Successful
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: '#1a1a1a',
          lineHeight: 1.6,
          fontSize: '1.05rem',
          fontWeight: 500
        }}
      >
        Thank you, <strong>{successModal.visitorName}</strong>!<br />
        Your details have been recorded — we will get back to you very soon with your new quote.
      </Typography>
    </Box>
  </>
)}
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};
<style>
{`
  @keyframes fadeIn {
    from { opacity: 0; transform: translate(-50%, -48%); }
    to { opacity: 1; transform: translate(-50%, -50%); }
  }
`}
</style>
export default IntakeForm;